import type { OrdersFilters, OrderResponse } from '@/services/ordersApi'

export interface OrderValidationDetail {
  field: string
  message: string
}

export function countActiveOrderFilters(filters: OrdersFilters): number {
  let count = 0

  if (filters.status && filters.status !== 'all') count += 1
  if (filters.search?.trim()) count += 1
  if (filters.channelInstanceId?.trim()) count += 1
  if (filters.from?.trim()) count += 1
  if (filters.to?.trim()) count += 1

  return count
}

export function getOrdersEmptyState(options: { total: number; hasActiveFilters: boolean }) {
  if (options.total > 0) return null

  if (options.hasActiveFilters) {
    return {
      title: 'No hay coincidencias',
      description: 'Prueba con menos filtros o limpia la búsqueda para ver más resultados.',
      primaryAction: 'Limpiar filtros',
      secondaryAction: 'Ir a conversaciones',
    }
  }

  return {
    title: 'Todavía no hay órdenes',
    description: 'Crea una desde una conversación o desde el catálogo para empezar a operar.',
    primaryAction: 'Abrir conversaciones',
    secondaryAction: 'Ir a productos',
  }
}

export function buildRepeatOrderUrl(order: Pick<OrderResponse, 'conversationId' | 'items' | 'source'>): string {
  const params = new URLSearchParams()

  if (order.conversationId) {
    params.set('conversationId', order.conversationId)
  }

  if (order.source) {
    params.set('source', order.source)
  }

  order.items.forEach((item) => {
    if (!item.productId) return
    params.append('productId', item.productId)
    params.append('quantity', String(Math.max(1, Number(item.quantity || 1))))
  })

  if (!params.has('productId')) {
    const firstProductId = order.items.find((item) => item.productId)?.productId ?? null
    if (firstProductId) {
      params.set('productId', firstProductId)
      params.set('quantity', '1')
    }
  }

  const query = params.toString()
  return query ? `/app/orders/new?${query}` : '/app/orders/new'
}

export interface BuildCreateOrderFromConversationOptions {
  conversationId: string
  salesContext?: import('@/services/conversationsApi').ConversationSalesContext | null
  contactName?: string | null
  customerPhone?: string | null
  channelInstanceId?: string | null
  channelType?: string | null
}

export function buildCreateOrderUrlFromConversation(options: BuildCreateOrderFromConversationOptions): string {
  const { conversationId, salesContext, contactName, customerPhone, channelInstanceId, channelType } = options
  const params = new URLSearchParams()
  params.set('conversationId', conversationId)
  params.set('source', 'checkout')

  if (channelInstanceId) {
    params.set('channelInstanceId', channelInstanceId)
  }
  if (channelType) {
    params.set('channelType', channelType)
  }

  const handoff = salesContext?.handoff
  const draft = salesContext?.activeOrderDraft

  // Prioritize customer info
  const name = handoff?.customerName || contactName
  if (name) params.set('customerName', name)
  const phone = handoff?.customerPhone || customerPhone
  if (phone) params.set('customerPhone', phone)

  // Prioritize fulfillment method
  const method = handoff?.fulfillmentMethod || draft?.fulfillmentMethod
  if (method) params.set('fulfillmentMethod', method)
  const address = handoff?.fulfillmentAddress || draft?.fulfillmentAddress
  if (address) params.set('fulfillmentAddress', address)

  // Items / products
  let hasItem = false
  if (draft?.items && draft.items.length > 0) {
    draft.items.forEach((it) => {
      if (it.productId) {
        params.append('productId', it.productId)
        params.append('quantity', String(Math.max(1, Number(it.quantity || 1))))
        hasItem = true
      } else if (it.title) {
        params.append('productName', it.title)
        params.append('quantity', String(Math.max(1, Number(it.quantity || 1))))
        hasItem = true
      }
    })
  }

  if (!hasItem && handoff?.items && handoff.items.length > 0) {
    handoff.items.forEach((it) => {
      if (it.name) {
        params.append('productName', it.name)
        params.append('quantity', String(Math.max(1, Number(it.quantity || 1))))
        hasItem = true
      }
    })
  }

  if (!hasItem && salesContext?.product?.id) {
    params.set('productId', salesContext.product.id)
    params.set('quantity', '1')
    hasItem = true
  } else if (!hasItem && salesContext?.product?.title) {
    params.set('productName', salesContext.product.title)
    params.set('quantity', '1')
    hasItem = true
  }

  if (!hasItem && salesContext?.lastMentionedProductId) {
    params.set('productId', salesContext.lastMentionedProductId)
    params.set('quantity', '1')
    hasItem = true
  } else if (!hasItem && salesContext?.lastMentionedProductName) {
    params.set('productName', salesContext.lastMentionedProductName)
    params.set('quantity', '1')
    hasItem = true
  }

  const query = params.toString()
  return `/app/orders/new?${query}`
}

export interface ChannelInstanceCandidate {
  id: string
  channelType: string
  instanceName?: string
  status?: string
}

export interface ResolveOrderChannelOptions {
  currentChannelInstanceId?: string | null
  conversationChannelInstanceId?: string | null
  conversationChannelType?: string | null
  urlChannelInstanceId?: string | null
  urlChannelType?: string | null
  instances?: ChannelInstanceCandidate[] | null
  hasConversationContext?: boolean
}

export function isWhatsAppChannelType(channelType: string | null | undefined): boolean {
  if (!channelType) return false
  const upper = channelType.toUpperCase()
  return upper === 'WHATSAPP' || upper === 'WHATSAPP_CLOUD'
}

export function resolveOrderChannelInstanceId(options: ResolveOrderChannelOptions): string {
  const {
    currentChannelInstanceId,
    conversationChannelInstanceId,
    conversationChannelType,
    urlChannelInstanceId,
    urlChannelType,
    instances = [],
    hasConversationContext = false,
  } = options

  const availableInstances = instances || []
  const targetChannelType = conversationChannelType || urlChannelType || null
  const targetInstanceId = conversationChannelInstanceId || urlChannelInstanceId || null

  // 0. If currentChannelInstanceId is already set and belongs to available instances:
  if (currentChannelInstanceId) {
    const currentInst = availableInstances.find((inst) => inst.id === currentChannelInstanceId)
    if (currentInst) {
      // If the conversation context is WhatsApp, but currentInst is WebChat,
      // it was an unintended default or leak; reject WebChat and re-resolve.
      const isMismatch = isWhatsAppChannelType(targetChannelType) && currentInst.channelType.toUpperCase() === 'WEBCHAT'
      if (!isMismatch) {
        return currentChannelInstanceId
      }
    }
  }

  // 1. Exact ID match from conversation or URL
  if (targetInstanceId) {
    const exactMatch = availableInstances.find((inst) => inst.id === targetInstanceId)
    if (exactMatch) {
      return exactMatch.id
    }
  }

  // 2. Match by channel type when conversation context is present
  if (hasConversationContext || targetChannelType) {
    if (isWhatsAppChannelType(targetChannelType)) {
      const matchedWhatsApp =
        availableInstances.find((inst) => inst.channelType.toUpperCase() === targetChannelType?.toUpperCase()) ||
        availableInstances.find((inst) => isWhatsAppChannelType(inst.channelType))

      if (matchedWhatsApp) {
        return matchedWhatsApp.id
      }
      // Never fallback to WebChat for WhatsApp conversations
      return targetInstanceId || ''
    }

    if (targetChannelType) {
      const matched = availableInstances.find(
        (inst) => inst.channelType.toUpperCase() === targetChannelType.toUpperCase()
      )
      if (matched) {
        return matched.id
      }
    }

    if (targetInstanceId) {
      return targetInstanceId
    }
  }

  // 3. Fallback for manual creation (no conversation)
  if (!hasConversationContext && !targetChannelType && availableInstances.length === 1) {
    return availableInstances[0].id
  }

  return ''
}


export function normalizeProductSearchText(text: string): string {
  if (!text) return ''
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

export function matchProductByNameOrSku<T extends { id: string; name: string; sku?: string | null }>(
  targetNameOrSku: string | null | undefined,
  products: T[]
): string | null {
  if (!targetNameOrSku || !products || products.length === 0) return null
  const target = normalizeProductSearchText(targetNameOrSku)
  if (!target) return null

  // 1. Exact match on normalized name or SKU
  const exact = products.find((p) => {
    const pName = normalizeProductSearchText(p.name)
    const pSku = p.sku ? normalizeProductSearchText(p.sku) : ''
    return pName === target || (pSku !== '' && pSku === target)
  })
  if (exact) return exact.id

  // 2. Substring match (either product name contains target, or target contains product name)
  const sub = products.find((p) => {
    const pName = normalizeProductSearchText(p.name)
    return pName.length >= 3 && (pName.includes(target) || target.includes(pName))
  })
  if (sub) return sub.id

  // 3. Significant word token overlap: all target words of >= 3 chars are in product name
  const targetTokens = target.split(/\s+/).filter((w) => w.length >= 3)
  if (targetTokens.length > 0) {
    const tokenMatch = products.find((p) => {
      const pName = normalizeProductSearchText(p.name)
      return targetTokens.every((word) => pName.includes(word))
    })
    if (tokenMatch) return tokenMatch.id
  }

  return null
}

export function buildOrderDetailUrl(orderId: string): string {
  return `/app/orders/${orderId}`
}

export function buildOrderReceiptUrl(orderId: string, autoPrint = false): string {
  const params = new URLSearchParams()
  if (autoPrint) {
    params.set('autoPrint', '1')
  }

  const query = params.toString()
  return query ? `/app/orders/${orderId}/receipt?${query}` : `/app/orders/${orderId}/receipt`
}

export function buildAbsoluteOrderUrl(orderId: string): string {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : ''
  return `${baseUrl}${buildOrderDetailUrl(orderId)}`
}

export function getOrderConversationBadge(conversationId: string | null | undefined) {
  return conversationId
    ? { label: 'Vinculada a conversación', tone: 'emerald' as const }
    : { label: 'Sin conversación vinculada', tone: 'slate' as const }
}

export function getOrderSourceBadge(source: OrderResponse['source']) {
  const normalized = buildOrderSourceLabel(source)
  return {
    label: `Fuente: ${normalized}`,
    tone:
      source === 'checkout'
        ? ('blue' as const)
        : source === 'catalog'
          ? ('violet' as const)
      : ('slate' as const),
  }
}

export function getOrderOriginBadge(params: {
  source: OrderResponse['source']
  conversationId: string | null | undefined
}) {
  if (params.source === 'checkout' && params.conversationId) {
    return { label: 'Origen: checkout materializado', tone: 'blue' as const }
  }

  if (params.conversationId) {
    return { label: 'Origen: conversación activa', tone: 'emerald' as const }
  }

  if (params.source === 'catalog') {
    return { label: 'Origen: catálogo', tone: 'violet' as const }
  }

  return { label: 'Origen: creación manual', tone: 'slate' as const }
}

export function isDeliveryLikeMethod(method: string) {
  return ['delivery', 'cash_on_delivery', 'shipping'].includes(method)
}

export function isPickupLikeMethod(method: string) {
  return ['pickup', 'store_pickup'].includes(method)
}

export function getFulfillmentModeLabel(method: string) {
  if (method === 'delivery') return 'Delivery'
  if (method === 'pickup') return 'Pickup'
  if (method === 'cash_on_delivery') return 'Envío cobro destino'
  if (method === 'shipping') return 'Envío nacional'
  if (method === 'store_pickup') return 'Retiro en tienda'
  return method || 'Sin definir'
}

export function isOrderEditable(status: OrderResponse['status']) {
  return status !== 'completed' && status !== 'cancelled'
}

/** Cancelar siempre deja trazabilidad: el motivo es obligatorio. */
export function isCancellationNoteValid(
  status: 'completed' | 'cancelled',
  note: string
): boolean {
  return status !== 'cancelled' || note.trim().length > 0
}

export function getOrderStatusBadge(status: OrderResponse['status']) {
  if (status === 'pending_confirmation') {
    return { label: 'Pendiente de confirmación', tone: 'amber' as const }
  }

  if (status === 'in_progress') {
    return { label: 'En proceso', tone: 'indigo' as const }
  }

  if (status === 'ready') {
    return { label: 'Lista para entregar', tone: 'emerald' as const }
  }

  if (status === 'completed') {
    return { label: 'Completada', tone: 'emerald' as const }
  }

  if (status === 'confirmed') {
    return { label: 'Confirmada', tone: 'blue' as const }
  }

  if (status === 'cancelled') {
    return { label: 'Cancelada', tone: 'rose' as const }
  }

  return { label: 'Borrador', tone: 'slate' as const }
}

export function getOrderStatusVisual(status: OrderResponse['status']) {
  if (status === 'pending_confirmation') return { icon: 'clock', accentTone: 'amber' as const }
  if (status === 'in_progress') return { icon: 'arrow', accentTone: 'indigo' as const }
  if (status === 'ready') return { icon: 'check', accentTone: 'emerald' as const }
  if (status === 'completed') return { icon: 'check', accentTone: 'emerald' as const }
  if (status === 'confirmed') return { icon: 'check', accentTone: 'blue' as const }
  if (status === 'cancelled') return { icon: 'ban', accentTone: 'rose' as const }
  return { icon: 'package', accentTone: 'slate' as const }
}

export function getOrderOperationalSubstate(status: OrderResponse['status']) {
  if (status === 'pending_confirmation') return 'Esperando validación'
  if (status === 'in_progress') return 'En preparación'
  if (status === 'ready') return 'Lista para entregar'
  if (status === 'completed') return 'Cerrada con éxito'
  if (status === 'confirmed') return 'Confirmada, lista para avanzar'
  if (status === 'cancelled') return 'Orden cancelada'
  return 'Borrador en edición'
}

export function getOrderOperationalProgress(status: OrderResponse['status']) {
  if (status === 'pending_confirmation') {
    return { label: 'Esperando validación', tone: 'amber' as const, icon: 'clock' as const }
  }

  if (status === 'in_progress') {
    return { label: 'En preparación', tone: 'indigo' as const, icon: 'arrow' as const }
  }

  if (status === 'ready') {
    return { label: 'Lista para entregar', tone: 'emerald' as const, icon: 'check' as const }
  }

  if (status === 'completed') {
    return { label: 'Cerrada con éxito', tone: 'emerald' as const, icon: 'check' as const }
  }

  if (status === 'confirmed') {
    return { label: 'Confirmada, lista para avanzar', tone: 'blue' as const, icon: 'check' as const }
  }

  if (status === 'cancelled') {
    return { label: 'Orden cancelada', tone: 'rose' as const, icon: 'ban' as const }
  }

  return { label: 'Borrador en edición', tone: 'slate' as const, icon: 'package' as const }
}

export interface OrderStepperStep {
  key: string
  label: string
  tone: 'emerald' | 'blue' | 'violet' | 'slate' | 'amber' | 'indigo' | 'rose'
  icon: 'clock' | 'arrow' | 'check' | 'package' | 'ban'
  /** Estados que comparten este paso (una columna de cocina agrupa confirmada y en proceso). */
  matches?: readonly string[]
}

export function getOrderProgressStepper(
  status: OrderResponse['status'],
  workflow?: ReadonlyArray<OrderStepperStep>
) {
  const steps: ReadonlyArray<OrderStepperStep> = workflow?.length
    ? workflow
    : [
        { key: 'validation', label: 'Validación', tone: 'amber', icon: 'clock' },
        { key: 'preparation', label: 'Preparación', tone: 'indigo', icon: 'arrow' },
        { key: 'closure', label: 'Cierre', tone: 'emerald', icon: 'check' },
      ]

  const workflowIndex = workflow?.length
    ? workflow.findIndex((step) => step.key === status || step.matches?.includes(status))
    : -1

  const activeIndex =
    workflowIndex >= 0
      ? workflowIndex
      : status === 'draft' || status === 'pending_confirmation'
        ? 0
        : status === 'confirmed' || status === 'in_progress'
          ? 1
          : status === 'ready' || status === 'completed'
            ? 2
            : 1

  return steps.map((step, index) => ({
    ...step,
    state: index < activeIndex ? ('done' as const) : index === activeIndex ? ('current' as const) : ('upcoming' as const),
    tone: status === 'cancelled' && index === activeIndex ? ('rose' as const) : step.tone,
  }))
}

const ORDER_VALIDATION_FIELD_LABELS: Record<string, string> = {
  source: 'origen',
  status: 'estado',
  conversationId: 'conversación',
  contactId: 'contacto',
  channelInstanceId: 'canal',
  customerName: 'nombre del cliente',
  customerPhone: 'teléfono del cliente',
  customerEmail: 'correo del cliente',
  fulfillmentMethod: 'método de entrega',
  fulfillmentAddress: 'dirección de entrega',
  fulfillmentReference: 'referencia de entrega',
  notes: 'observaciones',
  idempotencyKey: 'clave de idempotencia',
  items: 'productos',
  'items.productId': 'producto',
  'items.quantity': 'cantidad',
}

export function humanizeOrderValidationField(field: string): string {
  const normalized = field.replace(/\.\d+\./g, '.').replace(/\.\d+$/g, '')
  return ORDER_VALIDATION_FIELD_LABELS[normalized] ?? ORDER_VALIDATION_FIELD_LABELS[field] ?? field
}

export function buildOrderValidationMessage(details: OrderValidationDetail[] | undefined | null): string {
  if (!details || details.length === 0) {
    return 'No se pudo crear la orden'
  }

  const labels = Array.from(
    new Set(
      details
        .map((detail) => humanizeOrderValidationField(detail.field))
        .filter((label) => Boolean(label?.trim()))
    )
  )

  if (labels.length === 0) {
    return 'No se pudo crear la orden'
  }

  return `Falta completar ${labels.length === 1 ? 'el campo' : 'los campos'}: ${labels.join(', ')}`
}

export function buildOrderSourceLabel(source: OrderResponse['source']): string {
  if (source === 'checkout') return 'Checkout'
  if (source === 'catalog') return 'Catálogo'
  return 'Manual'
}

export const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  pending_confirmation: 'Pendiente',
  confirmed: 'Confirmada',
  in_progress: 'En proceso',
  ready: 'Lista para entregar',
  completed: 'Completada',
  cancelled: 'Cancelada',
}

export const STATUS_OPTIONS: Array<string> = [
  'all',
  'draft',
  'pending_confirmation',
  'confirmed',
  'in_progress',
  'ready',
  'completed',
  'cancelled',
]

/**
 * Monto de una orden con sus centavos: redondear a enteros mostraba $2.50 como "3" y $15.99 como "16", distinto de lo
 * que el asistente le dijo al cliente. En dólares se usa el mismo formato que el chat ("$15.99").
 */
export function formatOrderMoney(cents: number, currency = 'USD'): string {
  const amount = (Number.isFinite(cents) ? cents : 0) / 100
  if (currency.toUpperCase() === 'USD') return `$${amount.toFixed(2)}`
  return new Intl.NumberFormat('es-VE', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount)
}

export function buildGoogleMapsAddressUrl(address: string | null | undefined): string | null {
  if (!address || !address.trim()) return null
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`
}

export function cleanPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return ''
  return phone.replace(/[^\d+]/g, '').replace(/^\+/, '')
}

export function buildOrderWhatsAppUrl(phone: string | null | undefined, message?: string): string | null {
  const clean = cleanPhoneNumber(phone)
  if (!clean) return null
  const query = message ? `?text=${encodeURIComponent(message)}` : ''
  return `https://wa.me/${clean}${query}`
}

export function formatOrderSummaryText(order: OrderResponse): string {
  const customer = order.customerName || order.customerPhone || 'Sin cliente'
  const method = order.fulfillmentMethod || 'Sin definir'
  const address = order.fulfillmentAddress || order.fulfillmentReference || 'No especificada'

  const itemsText = order.items && order.items.length > 0
    ? order.items.map((it) => `• ${it.name} x${it.quantity} - ${formatOrderMoney(it.lineTotalCents, it.currency)}`).join('\n')
    : '• Sin items'

  return [
    `🛍️ *Orden #${order.orderNumber}*`,
    `👤 *Cliente:* ${customer}`,
    `📦 *Entrega:* ${method}`,
    `📍 *Dirección/Ref:* ${address}`,
    order.notes ? `📝 *Observaciones:* ${order.notes}` : null,
    '',
    `*Productos:*`,
    itemsText,
    '',
    `💰 *Total:* ${formatOrderMoney(order.totalCents, order.currency)}`,
  ].filter((line) => line !== null).join('\n')
}

export type WhatsAppNotificationType = 'confirmed' | 'in_progress' | 'ready' | 'completed' | 'cancelled'

export function buildOrderWhatsAppNotificationMessage(
  order: OrderResponse,
  type: WhatsAppNotificationType
): string {
  const name = order.customerName || 'estimado cliente'
  const total = formatOrderMoney(order.totalCents, order.currency)

  switch (type) {
    case 'confirmed':
      return `¡Hola ${name}! 👋 Tu orden *#${order.orderNumber}* ha sido *confirmada* y está en preparación. Total: ${total}. ¡Muchas gracias por tu compra!`
    case 'in_progress':
      return `¡Hola ${name}! 🚚 Tu orden *#${order.orderNumber}* ya se encuentra *en camino*${order.fulfillmentAddress ? ` a ${order.fulfillmentAddress}` : ''}. Te estaremos avisando ante cualquier novedad.`
    case 'ready': {
      const pickup = (order.fulfillmentMethod || '').toLowerCase()
      const isPickup = ['pickup', 'store_pickup', 'retiro', 'retiro_en_tienda'].includes(pickup)
      return isPickup
        ? `¡Hola ${name}! ✅ Tu orden *#${order.orderNumber}* ya está *lista para retirar*. Te esperamos en el local para entregártela.`
        : `¡Hola ${name}! ✅ Tu orden *#${order.orderNumber}* ya está *lista* y sale en camino${order.fulfillmentAddress ? ` a ${order.fulfillmentAddress}` : ''}.`
    }
    case 'completed':
      return `¡Hola ${name}! ✅ Tu orden *#${order.orderNumber}* ha sido *entregada con éxito*. Esperamos que disfrutes tus productos. ¡Fue un gusto atenderte!`
    case 'cancelled':
      return `Hola ${name}. Te informamos que tu orden *#${order.orderNumber}* ha sido *cancelada*. Si tienes alguna pregunta, por favor escríbenos por este medio.`
  }
}

export interface NextRecommendedAction {
  nextStatus: OrderResponse['status']
  label: string
  tone: 'emerald' | 'blue' | 'indigo'
  description: string
}

export function getOrderNextRecommendedAction(
  status: OrderResponse['status'],
  primaryAction?: { label: string; next: OrderResponse['status'] } | null
): NextRecommendedAction | null {
  if (primaryAction) {
    return {
      nextStatus: primaryAction.next,
      label: primaryAction.label,
      tone: primaryAction.next === 'confirmed' ? 'blue' : primaryAction.next === 'completed' ? 'emerald' : 'indigo',
      description:
        primaryAction.next === 'confirmed'
          ? 'Valida los datos y confirma el pedido para empezar a prepararlo.'
          : primaryAction.next === 'ready'
            ? 'Avisa cuando esté listo para retirar o salir a entrega.'
            : primaryAction.next === 'in_progress'
              ? 'Pasa la orden a preparación en cocina o almacén.'
              : 'Marca el pedido como entregado y finalizado.',
    }
  }

  if (status === 'draft' || status === 'pending_confirmation') {
    return {
      nextStatus: 'confirmed',
      label: 'Confirmar Orden',
      tone: 'blue',
      description: 'Valida los datos y confirma el pedido para avanzar a preparación.',
    }
  }

  if (status === 'confirmed') {
    return {
      nextStatus: 'in_progress',
      label: 'Iniciar Preparación / Despacho',
      tone: 'indigo',
      description: 'Pasa la orden a preparación en cocina/almacén o despacho en camino.',
    }
  }

  if (status === 'in_progress') {
    return {
      nextStatus: 'completed',
      label: 'Completar y Entregar',
      tone: 'emerald',
      description: 'Marca el pedido como entregado y finalizado con éxito.',
    }
  }

  if (status === 'ready') {
    return {
      nextStatus: 'completed',
      label: 'Entregar pedido',
      tone: 'emerald',
      description: 'Entrega el pedido al cliente y cierra la orden.',
    }
  }

  return null
}

export const ORDER_CANCELLATION_REASONS = [
  'Cliente desistió de la compra',
  'Producto sin stock disponible',
  'Dirección fuera de zona de cobertura',
  'Orden duplicada o de prueba',
  'No se logró confirmar el pago',
  'Otro motivo',
] as const




